/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  type DispatchSinglePlanSummaryType,
  type DispatchPlanHistoryResponseItemType,
} from '../DispatchSummary/types/dispatch-summary.types';
import { useAppSelector } from 'store/hook';
import { useCurrentUserInfo } from 'hooks/useCurrentUserInfo';
import useCustomerInfo from 'hooks/useCustomerInfo';
import FleetPlanningHandler from 'handlers/fleet-planning.handler';
import dayjs, { type Dayjs } from 'dayjs';
import { HistoryList } from './components/HistoryList';
import { PlanDetailView } from './components/PlainDetailView';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getActivePlanForTodayFromPlanHistory } from '../DispatchSummary/utils/dataGetters';
import { formatStartEndDateUTC } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

export type ExtendedDispatchPlanHistoryResponseItemType =
  DispatchPlanHistoryResponseItemType & {
    id: string;
    status: 'active' | 'draft';
  };

export interface DispatchPlanIds {
  planId: string;
  historyId: string;
}

export const DispatchPlanHistory = ({
  date,
  selectedDateRange,
  onClose,
  handleDateChange,
}: {
  date: dayjs.Dayjs | null;
  selectedDateRange: string;
  onClose: () => void;
  handleDateChange: (
    range: string,
    date: Dayjs | null,
    shouldFetchActivePlan: boolean,
    isButtonClick?: boolean
  ) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const currentUser = useCurrentUserInfo();
  const { customerTz } = useCustomerInfo();

  const fleetPlanningHandler = new FleetPlanningHandler();
  const [historyData, setHistoryData] = useState<
    ExtendedDispatchPlanHistoryResponseItemType[]
  >([]);

  const [activePlan, setActivePlan] = useState({
    planId: '',
    historyId: '',
  });

  const [selectedPlan, setSelectedPlan] =
    useState<ExtendedDispatchPlanHistoryResponseItemType | null>(null);

  const [planSummaryMap, setPlanSummaryMap] = useState<
    Record<string, DispatchSinglePlanSummaryType>
  >({});

  const [historyLoading, setHistoryLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const todayInTz = useMemo(() => {
    return dayjs().tz(customerTz);
  }, [customerTz]);

  const handleViewPlanDetails = async (
    paramHistoryDataItem: ExtendedDispatchPlanHistoryResponseItemType
  ) => {
    try {
      setDetailsLoading(true);
      if (planSummaryMap[paramHistoryDataItem.planId]) {
        setSelectedPlan(paramHistoryDataItem);
      } else {
        const planDate = dayjs
          .utc(paramHistoryDataItem.startDateWithTime)
          .format('YYYY-MM-DD');
        const response =
          await fleetPlanningHandler.getDispatchPlanSummaryByPlanId(
            planDate,
            false,
            'preview',
            paramHistoryDataItem.planId,
            paramHistoryDataItem?.planName ?? ''
          );
        setPlanSummaryMap((prev) => ({
          ...prev,
          [paramHistoryDataItem.planId]: response,
        }));
        setSelectedPlan(paramHistoryDataItem);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setHistoryLoading(true);
      const result = await fleetPlanningHandler.getDispatchPlanHistory();
      const temp: ExtendedDispatchPlanHistoryResponseItemType[] = result?.map(
        (el, idx: number) => ({
          ...el,
          id: el.historyId,
          planName: el?.planName ?? `Plan ${idx + 1}`,
          status: idx === 0 ? 'active' : 'draft',
        })
      );

      const topRow = temp[0];
      await handleViewPlanDetails(topRow);
      setHistoryData(temp);

      const latestActivePlan = getActivePlanForTodayFromPlanHistory(
        result,
        todayInTz
      );

      if (latestActivePlan) {
        setActivePlan({
          historyId: latestActivePlan.historyId,
          planId: latestActivePlan.planId,
        });
      }
    } catch (error) {
      console.error(error);
      setHistoryData([]);
      setActivePlan({
        planId: '',
        historyId: '',
      });
      setSelectedPlan(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSetDispatchSummaryPlan = async (
    paramHistoryDataItem: ExtendedDispatchPlanHistoryResponseItemType,
    isActive: boolean,
    setActivePlan: boolean,
    paramSelectedRange: string,
    paramSelectedDate: Dayjs | null
  ) => {
    try {
      const startDateTime = todayInTz.toISOString();
      const endDateTime = todayInTz.endOf('day').toISOString();
      const startOfDay = todayInTz.startOf('day').toISOString();

      if (!currentUser) return;
      if (setActivePlan) {
        // set plan as active
        await fleetPlanningHandler.setActiveApprovedDispatchPlan(
          paramHistoryDataItem.historyId,
          currentUser.userId,
          startDateTime,
          endDateTime,
          startOfDay
        );

        // revert to todya and fetch data
        await handleDateChange('Today', null, true);
        return;
      } else {
        const { startUtcISO } = formatStartEndDateUTC(
          paramSelectedRange,
          paramSelectedDate,
          paramSelectedDate,
          customerTz
        );
        if (!startUtcISO) return;
        // fetch actual data of that date
        const planDate = dayjs.utc(startUtcISO).format('YYYY-MM-DD');
        await Promise.all([
          handleDateChange(paramSelectedRange, paramSelectedDate, false, true),
          fleetPlanningHandler.getDispatchPlanSummaryByPlanId(
            planDate,
            true,
            'preview',
            paramHistoryDataItem.planId,
            paramHistoryDataItem.planName ?? ''
          ),
        ]);
      }

      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (accessToken) {
      void fetchData();
    }
  }, [accessToken]);

  if (historyLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        width={'100%'}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <HistoryList
        date={date}
        selectedDateRange={selectedDateRange}
        historyData={historyData}
        activePlan={activePlan}
        selectedPlan={selectedPlan}
        handleViewPlanDetails={handleViewPlanDetails}
        handleSetDispatchSummaryPlan={handleSetDispatchSummaryPlan}
      />
      <Box
        flex={1}
        sx={{
          overflowY: 'auto',
          padding: 2,
        }}
      >
        {!selectedPlan?.planId ? (
          <Typography color="#94a3b8">
            {t('select_a_plan_to_view_details')}
          </Typography>
        ) : (
          <>
            {detailsLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <CircularProgress size={32} />
              </Box>
            ) : (
              <PlanDetailView
                planSummary={planSummaryMap[selectedPlan.planId] ?? {}}
                activePlan={activePlan}
                selectedPlan={selectedPlan}
                handleSetDispatchSummaryPlan={handleSetDispatchSummaryPlan}
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};
